import { Icon } from '@hologram-dimension/icon';
import type { IconNames } from '@hologram-dimension/icons';
import ColorTokens, { ColorTokenNames } from '@hologram-dimension/tokens/colors';
import hyperSimCardImg from 'assets/img/new_customer_landing/hyper-sim-card.webp?&imagetools';
import simCardImg from 'assets/img/new_customer_landing/sim-card.webp?&imagetools';
import { clsx } from 'clsx/lite';
import { BackgroundGradient, Picture } from 'components';
import { JSX } from 'react';
import type { AllowedBackgroundGradients } from '../BackgroundGradient/BackgroundGradient';
import styles from './CardButton.module.css';

type JSXA = JSX.IntrinsicElements['a'];

interface CardButtonProps {
  className?: string;
  variant?: 'link' | 'form';
  decorativeIcon?: React.ReactNode; // TODO: DDS Icon string should be allowed
  backgroundGradient?: AllowedBackgroundGradients;
  children?: React.ReactNode;
  descriptiveIcon: IconNames;
  descriptiveIconFill: ColorTokenNames;
  descriptiveIconHoverFill: ColorTokenNames;
  descriptivePicture?: 'hyper' | 'sim';
  onClick?: AnyFunction;
  href?: string;
}
type AllCardButtonProps = CardButtonProps & Pick<JSXA, Exclude<keyof JSXA, keyof CardButtonProps>>;

const CardButton: React.FC<AllCardButtonProps> = ({
  variant = 'link',
  className,
  backgroundGradient = 'default',
  children,
  descriptiveIcon,
  descriptiveIconFill,
  descriptiveIconHoverFill,
  descriptivePicture,
  onClick,
  href,
  ...rest
}) => {
  const iconFillStyle = {
    '--decriptive-icon-fill': ColorTokens[descriptiveIconFill],
    '--decriptive-icon-hover-fill': ColorTokens[descriptiveIconHoverFill],
  } as React.CSSProperties;

  const content = (
    <div className={clsx(styles.wrapper, className)}>
      <BackgroundGradient
        backgroundGradient={backgroundGradient}
        backgroundClass={styles.background}
        overlayGrid
        className={styles.backgroundGradient}
        pinTo="desktop"
        overrideGridSize={70}
      >
        <div className={styles.contents}>
          <div className={styles.descriptivePictureWrapper}>
            {descriptivePicture && (
              <Picture
                image={descriptivePicture === 'hyper' ? hyperSimCardImg : simCardImg}
                alt=""
                setDimensions={false}
                imgClassName={styles.backgroundImage}
              />
            )}
          </div>
          <div style={iconFillStyle} className={styles.descriptiveIcon}>
            <Icon name={descriptiveIcon} color="currentColor" size="large" />
          </div>
          <div className={styles.caption}>{children}</div>
          <div className={styles.continueButton}>
            <Icon
              name={variant === 'link' ? 'ArrowEastShort' : 'Envelope'}
              color="currentColor"
              size="large"
            />
          </div>
        </div>
      </BackgroundGradient>
    </div>
  );

  if (href) {
    return (
      <a className={styles.link} href={href} {...rest}>
        {content}
      </a>
    );
  }

  return (
    <button className={styles.button} type="button" onClick={onClick}>
      {content}
    </button>
  );
};

export default CardButton;
